// src/utils/kindLabel.js
//
// THE ONE PRINTING OF A TRIO, on the browser side — a DECLARED PORT of
// `facility/services/kind_labels.trio_label` (Q-trios-and-pairing-photos,
// Fable, operator agreed 2026-10-14).
//
// Trios were already representable — a `group` lot of three for sale, a
// `group` pairing of 1.2 for breeding — and the operator wants that SAID:
// wherever a pairing's or a lot's kind is printed, a group of exactly one
// male and two females reads "Trio (1.2)", two males and one female
// "Trio (2.1)". Every other group keeps its surface's wording ("Group of
// 4"), and a pair stays a pair.
//
// The Pairings card prints the SERVER's `kind_label`; this port exists for
// the surfaces that print a lot's kind from the members' genders in the
// browser (the storefront lot card, the cart, the lots panel). Both sides
// are pinned to one table: `facility/tests/test_pairing_photos_trio.py`
// and `src/utils/__tests__/kindLabel.test.js`. Change one, change both.
//
// SEX IS THE RECORDED SEX (`gender`: 0 male, 1 female, anything else
// unsexed). An unsexed member is counted as neither, so a group of three
// with one unsexed animal is a group, not a trio.

const MALE = 0;
const FEMALE = 1;

/**
 * "Trio (1.2)" / "Trio (2.1)" for exactly three sexed animals in those
 * ratios, else null. The caller decides whether the thing IS a group.
 *
 * @param {Array<number|null|undefined>} genders
 * @returns {string|null}
 */
export function trioLabel(genders) {
  const list = Array.isArray(genders) ? genders : [];
  const males = list.filter((g) => g === MALE).length;
  const females = list.filter((g) => g === FEMALE).length;
  if (list.length !== 3 || males + females !== 3) return null;
  if (males === 1 && females === 2) return 'Trio (1.2)';
  if (males === 2 && females === 1) return 'Trio (2.1)';
  return null;
}

/**
 * A pinned GROUP lot's kind: a trio when its members say so, otherwise
 * today's "Group of N". (A pair and a stock bundle keep their own words at
 * the call site — a bundle has no members to count.)
 *
 * @param {Array<{gender?: number}>} members
 * @returns {string}
 */
export function groupLotLabel(members) {
  const list = Array.isArray(members) ? members : [];
  return trioLabel(list.map((m) => m?.gender)) || `Group of ${list.length}`;
}

/**
 * A PAIRING's kind from its members' ROLES — a DECLARED PORT of
 * `facility/services/pairing_members.kind_for`, the one derivation
 * (FABLE_QUEUE "Q-kind-for-at-create", operator ruled (a), 2026-10-27).
 * Exactly one sire and one dam (and nothing else) is a `pair`; any other
 * membership — more than one of a role, a single member, a member whose
 * role is unknown — is a `group`. No members: `null` (the counts cannot
 * decide; the server keeps the kind it was sent, or its default).
 *
 * The New pairing form prints it as a READ-OUT and sends no kind: the
 * server derives the kind it writes, and refuses a body kind the members
 * contradict. Pinned to the server's `KindForTableTests` rows
 * (`src/utils/__tests__/kindLabel.test.js`). Change one, change both.
 *
 * @param {Array<string>} roles
 * @returns {'pair'|'group'|null}
 */
export function kindFor(roles) {
  const list = (Array.isArray(roles) ? roles : []).map(String);
  if (!list.length) return null;
  const sires = list.filter((role) => role === 'sire').length;
  const dams = list.filter((role) => role === 'dam').length;
  return sires === 1 && dams === 1 && list.length === 2 ? 'pair' : 'group';
}

/**
 * A pairing kind as the Pairings card prints it — a port of
 * `kind_labels.pairing_kind_label`: "Known pair", or for a group "Trio
 * (1.2)" / "Trio (2.1)" when its members' recorded sexes say so, else
 * "Group". `genders` must be EVERY member's (an unknown one reads as
 * unsexed, so the label falls back to "Group" rather than claim a trio).
 *
 * @param {'pair'|'group'} kind
 * @param {Array<number|null|undefined>} genders
 * @returns {string}
 */
export function pairingKindLabel(kind, genders) {
  if (kind !== 'group') return 'Known pair';
  return trioLabel(genders) || 'Group';
}
